import React from 'react';
// Direct path, not the '@/components' barrel (it pulls the router in jest).
import PhotoTileGrid from '@/pages/shipments/PhotoTileGrid';
import {
  SandboxTimelineEvent,
  SandboxWebhookDelivery,
} from '@/services/api/sandbox';
import { DeliveryStatusPill, formatTime, statusLabel } from './sandboxUi';

const MEDIA_LABEL: Record<string, string> = {
  PICKUP_PROOF: 'Pickup proof',
  DELIVERY_PROOF: 'Delivery proof',
  FAILED_PROOF: 'Failed proof',
  RETURN_PROOF: 'Return proof',
  FAILED_RETURN_PROOF: 'Failed return proof',
  SIGNATURE: 'Signature',
};

// A webhook's event is built from its timeline row, but its `occurredAt` can
// trail the row by a few ms (the create path stamps its own time): take the
// same status at the nearest time, within a second.
const MATCH_WINDOW_MS = 1000;

// One event can have a copy per endpoint (the partner switched endpoints and
// it was retried): the event arrived if any copy did, else the copy on the
// active endpoint is the one that counts.
const copyRank = (d: SandboxWebhookDelivery) =>
  d.delivery.status === 'DONE' ? 2 : d.endpoint?.disabled ? 0 : 1;

const deliveryFor = (
  deliveries: SandboxWebhookDelivery[],
  status: string,
  occurredAt: string
) => {
  const at = Date.parse(occurredAt);
  let best: SandboxWebhookDelivery | null = null;
  let bestGap = MATCH_WINDOW_MS + 1;
  for (const d of deliveries) {
    if (d.eventStatus !== status || !d.occurredAt) continue;
    const gap = Math.abs(Date.parse(d.occurredAt) - at);
    if (
      gap < bestGap ||
      (gap === bestGap && best && copyRank(d) > copyRank(best))
    ) {
      best = d;
      bestGap = gap;
    }
  }
  return best;
};

/** An event's proof photos grouped by type, in the order they were taken. */
const mediaByType = (ev: SandboxTimelineEvent) => {
  const groups = new Map<string, string[]>();
  for (const m of ev.media ?? []) {
    if (!m.url) continue;
    groups.set(m.type, [...(groups.get(m.type) ?? []), m.url]);
  }
  return groups;
};

/**
 * The shipment's timeline, newest first, on a rail: each event with the
 * webhook it produced (matched on status + time) and its proof photos (the
 * same ones that webhook carried in `event.media`). Same rail construction as the Shipments drawer: the dot and
 * a flex-1 connector in one column, so the line stays centred by layout.
 */
const SandboxTimeline: React.FC<{
  events: SandboxTimelineEvent[];
  /** null while the deliveries are not loaded — no "No webhook" guess then. */
  deliveries: SandboxWebhookDelivery[] | null;
}> = ({ events, deliveries }) => (
  <ol className="mt-4">
    {events.map((ev, i) => {
      const delivery = deliveries
        ? deliveryFor(deliveries, ev.status, ev.occurredAt)
        : null;
      const media = mediaByType(ev);
      const last = i === events.length - 1;
      return (
        <li key={ev.id} className="flex gap-3">
          <div className="flex flex-col items-center self-stretch">
            <span
              aria-hidden="true"
              className={`mt-1 h-3 w-3 shrink-0 rounded-full border-2 ${
                i === 0
                  ? 'border-primary bg-primary ring-4 ring-(--primary-alpha-10)'
                  : 'border-stroke-sub-300 bg-bg-white-0'
              }`}
            />
            {!last && <span className="my-1 w-0.5 flex-1 bg-stroke-sub-300" />}
          </div>
          <div className={`min-w-0 flex-1 ${last ? '' : 'pb-5'}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-semibold text-text-strong-950">
                {statusLabel(ev.status)}
              </span>
              <span className="text-xs tabular-nums text-text-sub-600">
                {formatTime(ev.occurredAt)}
              </span>
              {delivery ? (
                <DeliveryStatusPill status={delivery.delivery.status} />
              ) : (
                deliveries && (
                  <span className="rounded-full bg-bg-weak-50 px-2.5 py-0.5 text-xs text-text-soft-400">
                    No webhook
                  </span>
                )
              )}
            </div>
            <p className="mt-0.5 text-sm text-text-sub-600">{ev.message}</p>
            <p className="mt-0.5 text-xs text-text-soft-400">
              by {ev.createdBy ?? 'system'}
              {ev.reason?.code ? ` · reason ${ev.reason.code}` : ''}
            </p>
            {media.size > 0 && (
              <div className="mt-2 w-fit max-w-full space-y-2 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5">
                {Array.from(media, ([type, urls]) => (
                  <div key={type}>
                    <p className="text-xs font-medium text-text-sub-600">
                      {MEDIA_LABEL[type] ?? type}
                    </p>
                    <PhotoTileGrid
                      urls={urls}
                      alt={MEDIA_LABEL[type] ?? type}
                      className="mt-1"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </li>
      );
    })}
  </ol>
);

export default SandboxTimeline;
